<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ListingImportsAPI from 'dashboard/api/listingImports';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  DRIFT_REVIEW_ACTIONS,
  IMPORT_RUN_MODES,
  IMPORT_RUN_STATUSES,
  TOMBSTONE_REVIEW_DECISIONS,
  driftFlowSteps,
  driftReviewPayload,
  importRunTone,
  normalizeDriftReviewResponse,
  normalizeDriftsResponse,
  normalizeRawRecord,
  normalizeRun,
  normalizeRunsResponse,
  normalizeTombstoneReviewResponse,
  operationPayload,
  rawStatusEntries,
  runSourceLabel,
  snapshotDiffSections,
  snapshotStateTone,
  tombstoneCandidateKey,
  tombstoneReviewPayload,
} from '../utils/listingImportOperations';

const { t } = useI18n();

const runs = ref([]);
const meta = ref({
  count: 0,
  total_count: 0,
  page: 1,
  per_page: 50,
  total_pages: 0,
});
const selectedRun = ref(null);
const rawRecord = ref(null);
const rawRecordId = ref('');
const operationReason = ref('');
const statusFilter = ref('');
const portalFilter = ref('');
const modeFilter = ref('');
const isLoading = ref(false);
const isLoadingRun = ref(false);
const isLoadingRaw = ref(false);
const actionKey = ref('');
const errorMessage = ref('');
const tombstoneSelectedExternalIds = ref([]);
const tombstoneReviewDecision = ref('suppressed');
const tombstoneReviewError = ref('');
const tombstoneReviewResult = ref(null);
const tombstoneDryRunResult = ref(null);
const drifts = ref([]);
const driftMeta = ref({ summary: {} });
const driftFilterStatus = ref('open');
const driftResolution = ref('mapping_fixed');
const isLoadingDrifts = ref(false);
const driftError = ref('');
const isPermissionDenied = ref(false);

function runStatusLabel(status) {
  switch (status) {
    case 'enqueued':
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.ENQUEUED');
    case 'processing':
    case 'running':
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.PROCESSING');
    case 'completed':
    case 'processed':
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.PROCESSED');
    case 'failed':
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.FAILED');
    case 'cancelled':
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.CANCELLED');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.STATUSES.UNKNOWN');
  }
}

function modeLabel(mode) {
  switch (mode) {
    case 'full':
      return t('PROPERTIES.IMPORT_OPERATIONS.MODES.FULL');
    case 'incremental':
      return t('PROPERTIES.IMPORT_OPERATIONS.MODES.INCREMENTAL');
    case 'replay':
      return t('PROPERTIES.IMPORT_OPERATIONS.MODES.REPLAY');
    case 'dry_run':
      return t('PROPERTIES.IMPORT_OPERATIONS.MODES.DRY_RUN');
    default:
      return mode || '-';
  }
}

const statusOptions = computed(() =>
  IMPORT_RUN_STATUSES.map(value => ({
    value,
    label: value
      ? runStatusLabel(value)
      : t('PROPERTIES.IMPORT_OPERATIONS.ALL_STATUS'),
  }))
);
const modeOptions = computed(() =>
  IMPORT_RUN_MODES.map(value => ({
    value,
    label: value
      ? modeLabel(value)
      : t('PROPERTIES.IMPORT_OPERATIONS.ALL_MODES'),
  }))
);
const summary = computed(() => ({
  total: meta.value.total_count || runs.value.length,
  active: runs.value.filter(run => run.active).length,
  retryable: runs.value.reduce(
    (sum, run) => sum + run.retryableRawRecordsCount,
    0
  ),
  quarantined: runs.value.filter(run => run.sourceQuarantined).length,
}));
const hasRuns = computed(() => runs.value.length > 0);
const hasPreviousPage = computed(() => Number(meta.value.page || 1) > 1);
const hasNextPage = computed(
  () => Number(meta.value.page || 1) < Number(meta.value.total_pages || 0)
);
const snapshotDetail = computed(() => rawRecord.value?.snapshotDetail || {});
const latestSnapshot = computed(() => {
  const snapshots = snapshotDetail.value?.snapshots || [];
  return snapshots[snapshots.length - 1] || null;
});
const snapshotSections = computed(() =>
  snapshotDiffSections(latestSnapshot.value)
);
const hasSnapshotDiff = computed(() => snapshotSections.value.length > 0);
const fieldRows = computed(() => rawRecord.value?.fieldRows || []);
const hasFieldRows = computed(() => fieldRows.value.length > 0);
const fieldErrors = computed(() => rawRecord.value?.fieldErrors || []);
const reconciliation = computed(() => rawRecord.value?.reconciliation || {});
const selectedSourceId = computed(() => selectedRun.value?.source?.id || '');
const hasDrifts = computed(() => drifts.value.length > 0);
const driftSummary = computed(
  () =>
    driftMeta.value?.summary || {
      total: drifts.value.length,
      open: drifts.value.filter(drift => drift.status === 'open').length,
      blocking_active: drifts.value.filter(drift => drift.blocking).length,
    }
);
const tombstoneCandidates = computed(
  () => selectedRun.value?.operationalSummary?.tombstones?.candidates || []
);
const tombstoneReviewCounts = computed(
  () =>
    selectedRun.value?.operationalSummary?.tombstones?.reviewCounts || {
      approved: 0,
      suppressed: 0,
      unreviewed: 0,
    }
);
const hasTombstoneCandidates = computed(
  () => tombstoneCandidates.value.length > 0
);
const selectedTombstoneExternalIds = computed(() => {
  const candidateExternalIds = new Set(
    tombstoneCandidates.value
      .map(candidate => candidate.externalId)
      .filter(Boolean)
  );

  return tombstoneSelectedExternalIds.value.filter(externalId =>
    candidateExternalIds.has(externalId)
  );
});
const selectedTombstoneCount = computed(
  () => selectedTombstoneExternalIds.value.length
);
const hasSelectedTombstoneCandidates = computed(
  () => selectedTombstoneCount.value > 0
);

const queryParams = () => ({
  page: meta.value.page || 1,
  per_page: meta.value.per_page || 50,
  status: statusFilter.value || undefined,
  portal: portalFilter.value || undefined,
  import_mode: modeFilter.value || undefined,
});

const fetchRuns = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  isPermissionDenied.value = false;
  try {
    const { data } = await ListingImportsAPI.runs(queryParams());
    const normalized = normalizeRunsResponse(data);
    runs.value = normalized.runs;
    meta.value = normalized.meta;
  } catch (error) {
    if ([401, 403].includes(error?.response?.status)) {
      isPermissionDenied.value = true;
      return;
    }

    errorMessage.value = t('PROPERTIES.IMPORT_OPERATIONS.ERRORS.LOAD');
  } finally {
    isLoading.value = false;
  }
};

const fetchDrifts = async (sourceId = selectedSourceId.value) => {
  isLoadingDrifts.value = true;
  driftError.value = '';
  try {
    const { data } = await ListingImportsAPI.drifts({
      status: driftFilterStatus.value || undefined,
      source_id: sourceId || undefined,
      per_page: 10,
    });
    const normalized = normalizeDriftsResponse(data);
    drifts.value = normalized.drifts;
    driftMeta.value = normalized.meta;
  } catch {
    driftError.value = t('PROPERTIES.IMPORT_OPERATIONS.ERRORS.DRIFTS');
  } finally {
    isLoadingDrifts.value = false;
  }
};

const applyFilters = () => {
  meta.value = { ...meta.value, page: 1 };
  fetchRuns();
};

const changePage = direction => {
  const nextPage = Number(meta.value.page || 1) + direction;
  if (nextPage < 1 || nextPage > Number(meta.value.total_pages || 0)) return;
  meta.value = { ...meta.value, page: nextPage };
  fetchRuns();
};

const loadRun = async run => {
  if (!run?.id) return;

  isLoadingRun.value = true;
  try {
    const { data } = await ListingImportsAPI.showRun(run.id);
    selectedRun.value = normalizeRun(data?.payload);
    tombstoneSelectedExternalIds.value = [];
    tombstoneReviewError.value = '';
    tombstoneReviewResult.value = null;
    tombstoneDryRunResult.value = null;
    await fetchDrifts(selectedRun.value?.source?.id);
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.ERRORS.RUN'));
  } finally {
    isLoadingRun.value = false;
  }
};

const runActionPayload = (action, id) =>
  operationPayload({
    action,
    id,
    reason:
      operationReason.value || t('PROPERTIES.IMPORT_OPERATIONS.DEFAULT_REASON'),
  });

async function performRunAction({ key, request, successMessage }) {
  actionKey.value = key;
  try {
    const { data } = await request();
    if (data?.payload) selectedRun.value = normalizeRun(data.payload);
    useAlert(successMessage);
    await fetchRuns();
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.ERROR'));
  } finally {
    actionKey.value = '';
  }
}

const retryRun = async run => {
  await performRunAction({
    key: `run:${run.id}:retry`,
    request: () =>
      ListingImportsAPI.retryRun(run.id, runActionPayload('retry', run.id)),
    successMessage: t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.RETRY'),
  });
};

const releaseRunQuarantine = async run => {
  await performRunAction({
    key: `run:${run.id}:release`,
    request: () =>
      ListingImportsAPI.releaseRunQuarantine(
        run.id,
        runActionPayload('release-run-quarantine', run.id)
      ),
    successMessage: t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.RELEASE'),
  });
};

const currentOperationReason = () =>
  operationReason.value || t('PROPERTIES.IMPORT_OPERATIONS.DEFAULT_REASON');

const reviewDrift = async (drift, action) => {
  if (!drift?.id || !DRIFT_REVIEW_ACTIONS.includes(action)) return;

  actionKey.value = `drift:${drift.id}:${action}`;
  try {
    const { data } = await ListingImportsAPI.reviewDrift(
      drift.id,
      action,
      driftReviewPayload({
        action,
        id: drift.id,
        reason: currentOperationReason(),
        resolution: driftResolution.value,
      })
    );
    const result = normalizeDriftReviewResponse(data);
    if (result.drift) {
      drifts.value = drifts.value.map(item =>
        item.id === result.drift.id ? result.drift : item
      );
    }
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.DRIFT_REVIEW'));
    await fetchDrifts();
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.ERROR'));
  } finally {
    actionKey.value = '';
  }
};

const setTombstoneReviewDecision = decision => {
  if (!TOMBSTONE_REVIEW_DECISIONS.includes(decision)) return;
  tombstoneReviewDecision.value = decision;
};

const isTombstoneCandidateSelected = candidate =>
  selectedTombstoneExternalIds.value.includes(candidate.externalId);

const toggleTombstoneCandidate = candidate => {
  const externalId = candidate?.externalId;
  if (!externalId) return;

  const nextSelection = new Set(tombstoneSelectedExternalIds.value);
  if (nextSelection.has(externalId)) {
    nextSelection.delete(externalId);
  } else {
    nextSelection.add(externalId);
  }
  tombstoneSelectedExternalIds.value = [...nextSelection];
  tombstoneReviewError.value = '';
};

const selectAllTombstoneCandidates = () => {
  tombstoneSelectedExternalIds.value = tombstoneCandidates.value
    .map(candidate => candidate.externalId)
    .filter(Boolean);
  tombstoneReviewError.value = '';
};

const clearTombstoneCandidateSelection = () => {
  tombstoneSelectedExternalIds.value = [];
};

const dryRunTombstoneReview = () => {
  tombstoneReviewResult.value = null;
  tombstoneReviewError.value = '';

  if (!hasSelectedTombstoneCandidates.value) {
    tombstoneReviewError.value = t(
      'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.NO_SELECTION'
    );
    return;
  }

  tombstoneDryRunResult.value = {
    status: tombstoneReviewDecision.value,
    reviewCount: selectedTombstoneCount.value,
    providerAcquisition: 'not_attempted',
    sideEffect: 'local_dry_run_only',
  };
};

const reviewTombstoneCandidates = async () => {
  if (!selectedRun.value?.id) return;

  tombstoneReviewResult.value = null;
  tombstoneDryRunResult.value = null;
  tombstoneReviewError.value = '';

  if (!hasSelectedTombstoneCandidates.value) {
    tombstoneReviewError.value = t(
      'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.NO_SELECTION'
    );
    return;
  }

  const reviewActionKey = `tombstone:${selectedRun.value.id}:${tombstoneReviewDecision.value}`;
  actionKey.value = reviewActionKey;
  try {
    const { data } = await ListingImportsAPI.reviewTombstoneCandidates(
      selectedRun.value.id,
      tombstoneReviewPayload({
        runId: selectedRun.value.id,
        decision: tombstoneReviewDecision.value,
        externalIds: selectedTombstoneExternalIds.value,
        reason: currentOperationReason(),
      })
    );

    if (data?.payload) selectedRun.value = normalizeRun(data.payload);
    tombstoneReviewResult.value = normalizeTombstoneReviewResponse(data);
    tombstoneSelectedExternalIds.value = [];
    useAlert(
      t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.SUCCESS', {
        count: tombstoneReviewResult.value.reviewCount,
      })
    );
    await fetchRuns();
  } catch (error) {
    tombstoneReviewError.value =
      error?.response?.data?.error ||
      t('PROPERTIES.IMPORT_OPERATIONS.ERRORS.TOMBSTONE_REVIEW');
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.ERROR'));
  } finally {
    actionKey.value = '';
  }
};

const loadRawRecord = async id => {
  const targetId = id || rawRecordId.value;
  if (!targetId) return;

  rawRecordId.value = String(targetId);
  isLoadingRaw.value = true;
  try {
    const { data } = await ListingImportsAPI.showRawRecord(targetId);
    rawRecord.value = data?.payload ? normalizeRawRecord(data.payload) : null;
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.ERRORS.RAW'));
  } finally {
    isLoadingRaw.value = false;
  }
};

const replayRawRecord = async record => {
  const targetId = record?.id || rawRecord.value?.id || rawRecordId.value;
  if (!targetId) return;

  actionKey.value = `raw:${targetId}:replay`;
  try {
    const { data } = await ListingImportsAPI.replayRawRecord(
      targetId,
      runActionPayload('replay-raw-record', targetId)
    );
    rawRecord.value = data?.payload
      ? normalizeRawRecord(data.payload)
      : rawRecord.value;
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.REPLAY'));
    await fetchRuns();
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.ERROR'));
  } finally {
    actionKey.value = '';
  }
};

const releaseRawRecordQuarantine = async record => {
  const targetId = record?.id || rawRecord.value?.id || rawRecordId.value;
  if (!targetId) return;

  actionKey.value = `raw:${targetId}:release`;
  try {
    const { data } = await ListingImportsAPI.releaseRawRecordQuarantine(
      targetId,
      runActionPayload('release-raw-quarantine', targetId)
    );
    rawRecord.value = data?.payload
      ? normalizeRawRecord(data.payload)
      : rawRecord.value;
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.RELEASE'));
    await fetchRuns();
  } catch {
    useAlert(t('PROPERTIES.IMPORT_OPERATIONS.TOASTS.ERROR'));
  } finally {
    actionKey.value = '';
  }
};

const counterValue = (run, key) => Number(run?.counters?.[key] || 0);
const isActionLoading = key => actionKey.value === key;
const snapshotStateLabel = state => {
  switch (state) {
    case 'empty':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.EMPTY');
    case 'partial':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.PARTIAL');
    case 'quarantined':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.QUARANTINED');
    case 'materialized':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.MATERIALIZED');
    case 'dry_run':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.DRY_RUN');
    case 'tombstoned':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.TOMBSTONED');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.STATES.UNKNOWN');
  }
};
const diffOperationLabel = operation => {
  switch (operation) {
    case 'added':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.BUCKETS.ADDED');
    case 'changed':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.BUCKETS.CHANGED');
    case 'overwritten':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.BUCKETS.OVERWRITTEN');
    case 'ignored':
      return t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.BUCKETS.IGNORED');
    default:
      return operation;
  }
};
const formatDiffValue = value => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
const normalizedPayloadEntries = snapshot =>
  Object.entries(snapshot?.normalizedPayload || {}).map(([field, value]) => ({
    field,
    value: formatDiffValue(value),
  }));

const fieldSourcesLabel = row =>
  row.sourcePaths?.length ? row.sourcePaths.join(', ') : '-';

const fieldSuggestionLabel = suggestion => {
  switch (suggestion) {
    case 'compare_duplicate_before_replay':
      return t(
        'PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTIONS.DUPLICATE'
      );
    case 'review_mapping_required_field':
      return t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTIONS.MISSING');
    case 'update_mapping_ack_drift':
      return t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTIONS.DRIFT');
    case 'fix_normalized_value':
      return t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTIONS.INVALID');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTIONS.REVIEW');
  }
};

const reconciliationStateLabel = state => {
  switch (state) {
    case 'duplicate':
      return t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.STATES.DUPLICATE');
    case 'reconciled':
      return t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.STATES.RECONCILED');
    case 'property_reconciled':
      return t(
        'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.STATES.PROPERTY_RECONCILED'
      );
    case 'blocked':
      return t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.STATES.BLOCKED');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.STATES.UNMATCHED');
  }
};

const reconciliationTone = state => {
  switch (state) {
    case 'duplicate':
      return 'warning';
    case 'reconciled':
    case 'property_reconciled':
      return 'positive';
    case 'blocked':
      return 'danger';
    default:
      return 'neutral';
  }
};

const driftStatusLabel = status => {
  switch (status) {
    case 'acknowledged':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.ACKNOWLEDGED');
    case 'resolved':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.RESOLVED');
    case 'dismissed':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.DISMISSED');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.OPEN');
  }
};

const driftTone = drift => {
  if (drift.status === 'resolved') return 'positive';
  if (drift.status === 'dismissed') return 'neutral';
  if (drift.blocking || drift.severity === 'critical') return 'danger';
  return 'warning';
};

const driftFlowStepLabel = step => {
  switch (step) {
    case 'detected':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.FLOW.DETECTED');
    case 'acknowledged':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.FLOW.ACKNOWLEDGED');
    case 'mapping':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.FLOW.MAPPING');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.FLOW.REPLAY');
  }
};

const driftFlowStepClass = state => {
  if (state === 'done') return 'bg-n-teal-4 text-n-teal-12';
  if (state === 'current') return 'bg-n-amber-4 text-n-amber-12';
  return 'bg-n-alpha-1 text-n-slate-11';
};

const driftActionLabel = action => {
  switch (action) {
    case 'acknowledge':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.ACTIONS.ACKNOWLEDGE');
    case 'resolve':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.ACTIONS.RESOLVE');
    case 'dismiss':
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.ACTIONS.DISMISS');
    default:
      return t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.ACTIONS.REOPEN');
  }
};

const tombstoneReviewStatusLabel = status => {
  switch (status) {
    case 'approved':
      return t(
        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.APPROVED'
      );
    case 'suppressed':
      return t(
        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.SUPPRESSED'
      );
    default:
      return t(
        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.UNREVIEWED'
      );
  }
};

const tombstoneReviewStatusTone = status => {
  switch (status) {
    case 'approved':
      return 'positive';
    case 'suppressed':
      return 'info';
    case 'unreviewed':
      return 'warning';
    default:
      return 'neutral';
  }
};

const formatDateTime = value => {
  if (!value) return t('PROPERTIES.IMPORT_OPERATIONS.NO_DATE');

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('PROPERTIES.IMPORT_OPERATIONS.NO_DATE');
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

onMounted(() => {
  fetchRuns();
  fetchDrifts();
});
</script>

<template>
  <main class="flex h-full min-h-0 flex-col overflow-auto bg-n-background">
    <LaisHeader
      :title="t('PROPERTIES.IMPORT_OPERATIONS.TITLE')"
      :description="t('PROPERTIES.IMPORT_OPERATIONS.SUBTITLE')"
    />

    <section class="border-b border-n-weak px-6 py-4">
      <div class="grid gap-3 md:grid-cols-4">
        <label class="flex flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.FILTERS.STATUS') }}
          <select
            v-model="statusFilter"
            class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value || 'all-status'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.FILTERS.MODE') }}
          <select
            v-model="modeFilter"
            class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          >
            <option
              v-for="option in modeOptions"
              :key="option.value || 'all-modes'"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label class="flex flex-col text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.FILTERS.PORTAL') }}
          <input
            v-model="portalFilter"
            type="search"
            class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
            @keyup.enter="applyFilters"
          />
        </label>
        <div class="flex items-end gap-2">
          <LaisPrimaryButton
            icon="i-lucide-filter"
            :label="t('PROPERTIES.IMPORT_OPERATIONS.FILTERS.APPLY')"
            @click="applyFilters"
          />
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            @click="fetchRuns"
          >
            {{ t('PROPERTIES.IMPORT_OPERATIONS.REFRESH') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </section>

    <section class="grid gap-3 px-6 py-4 md:grid-cols-4">
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.SUMMARY.RUNS') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ summary.total }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.SUMMARY.ACTIVE') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ summary.active }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.SUMMARY.RETRYABLE') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-ruby-11">
          {{ summary.retryable }}
        </p>
      </LaisCard>
      <LaisCard class="p-4">
        <p class="text-xs text-n-slate-11">
          {{ t('PROPERTIES.IMPORT_OPERATIONS.SUMMARY.QUARANTINED') }}
        </p>
        <p class="mt-1 text-xl font-semibold text-n-amber-11">
          {{ summary.quarantined }}
        </p>
      </LaisCard>
    </section>

    <section class="grid min-h-0 gap-4 px-6 pb-6 xl:grid-cols-[1.4fr_0.9fr]">
      <div>
        <p v-if="errorMessage" class="mb-3 text-sm text-n-ruby-11">
          {{ errorMessage }}
        </p>
        <div
          v-if="isPermissionDenied"
          class="py-10 text-center text-sm text-n-ruby-11"
        >
          {{ t('PROPERTIES.IMPORT_OPERATIONS.PERMISSION') }}
        </div>
        <div
          v-else-if="isLoading"
          class="py-10 text-center text-sm text-n-slate-11"
        >
          {{ t('PROPERTIES.IMPORT_OPERATIONS.STATES.LOADING') }}
        </div>
        <div
          v-else-if="!hasRuns"
          class="py-10 text-center text-sm text-n-slate-11"
        >
          {{ t('PROPERTIES.IMPORT_OPERATIONS.STATES.EMPTY') }}
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-n-slate-11">
              <th class="py-2">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RUN') }}
              </th>
              <th>{{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.STATUS') }}</th>
              <th>{{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.COUNTERS') }}</th>
              <th>{{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RAW') }}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="run in runs"
              :key="run.id"
              class="border-b border-n-weak align-top"
            >
              <td class="py-3">
                <p class="font-medium text-n-slate-12">
                  {{ runSourceLabel(run) }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RUN_META', {
                      id: run.id,
                      mode: modeLabel(run.importMode),
                      date: formatDateTime(run.createdAt),
                    })
                  }}
                </p>
              </td>
              <td class="py-3">
                <div class="flex flex-wrap gap-1">
                  <LaisStatusBadge
                    :label="runStatusLabel(run.status)"
                    :tone="importRunTone(run.status)"
                    dot
                  />
                  <LaisStatusBadge
                    v-if="run.active"
                    :label="
                      t('PROPERTIES.IMPORT_OPERATIONS.STATES.RUNNING_BADGE')
                    "
                    tone="info"
                  />
                </div>
              </td>
              <td class="py-3 text-xs text-n-slate-11">
                {{
                  t('PROPERTIES.IMPORT_OPERATIONS.TABLE.CREATED_UPDATED', {
                    created: counterValue(run, 'created'),
                    updated: counterValue(run, 'updated'),
                  })
                }}
              </td>
              <td class="py-3">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="entry in rawStatusEntries(run)"
                    :key="`${run.id}-${entry.status}`"
                    class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                  >
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RAW_STATUS_COUNT', {
                        status: entry.status,
                        count: entry.count,
                      })
                    }}
                  </span>
                </div>
                <p
                  v-if="run.retryableRawRecordsCount"
                  class="mt-1 text-xs text-n-ruby-11"
                >
                  {{
                    t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RETRYABLE_RAW', {
                      count: run.retryableRawRecordsCount,
                    })
                  }}
                </p>
                <p
                  v-if="counterValue(run, 'failed')"
                  class="mt-1 text-xs text-n-ruby-11"
                >
                  {{
                    t('PROPERTIES.IMPORT_OPERATIONS.TABLE.FAILED_ITEMS', {
                      count: counterValue(run, 'failed'),
                    })
                  }}
                </p>
              </td>
              <td class="py-3 text-right">
                <div class="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                    @click="loadRun(run)"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.DETAILS') }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                    :disabled="
                      !run.retryableRawRecordsCount ||
                      isActionLoading(`run:${run.id}:retry`)
                    "
                    @click="retryRun(run)"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.RETRY') }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                    :disabled="
                      !run.sourceQuarantined ||
                      isActionLoading(`run:${run.id}:release`)
                    "
                    @click="releaseRunQuarantine(run)"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.RELEASE') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="mt-4 flex items-center justify-between text-sm">
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="!hasPreviousPage"
            @click="changePage(-1)"
          >
            {{ t('PROPERTIES.IMPORT_OPERATIONS.PAGINATION.PREVIOUS') }}
          </button>
          <span class="text-n-slate-11">
            {{
              t('PROPERTIES.IMPORT_OPERATIONS.PAGINATION.PAGE', {
                page: meta.page || 1,
                total: meta.total_pages || 0,
              })
            }}
          </span>
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 disabled:opacity-50"
            :disabled="!hasNextPage"
            @click="changePage(1)"
          >
            {{ t('PROPERTIES.IMPORT_OPERATIONS.PAGINATION.NEXT') }}
          </button>
        </div>
      </div>

      <aside class="flex flex-col gap-4">
        <LaisCard class="p-4">
          <label class="flex flex-col text-xs text-n-slate-11">
            {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTION_REASON') }}
            <textarea
              v-model="operationReason"
              rows="3"
              class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
            />
          </label>
        </LaisCard>

        <LaisCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.RUN_DETAILS.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  selectedRun
                    ? t('PROPERTIES.IMPORT_OPERATIONS.RUN_DETAILS.SELECTED', {
                        id: selectedRun.id,
                      })
                    : t('PROPERTIES.IMPORT_OPERATIONS.RUN_DETAILS.EMPTY')
                }}
              </p>
            </div>
            <span
              v-if="isLoadingRun"
              class="i-lucide-loader-circle size-4 animate-spin text-n-slate-11"
            />
          </div>

          <div v-if="selectedRun" class="mt-4 flex flex-col gap-3">
            <div class="grid grid-cols-2 gap-2 text-xs text-n-slate-11">
              <div>
                <p>{{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.STATUS') }}</p>
                <p class="font-semibold text-n-slate-12">
                  {{ runStatusLabel(selectedRun.status) }}
                </p>
              </div>
              <div>
                <p>{{ t('PROPERTIES.IMPORT_OPERATIONS.TABLE.RAW') }}</p>
                <p class="font-semibold text-n-slate-12">
                  {{ selectedRun.latestRawRecords.length }}
                </p>
              </div>
            </div>
            <div
              v-for="record in selectedRun.latestRawRecords"
              :key="record.id"
              class="rounded-lg border border-n-weak p-3"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      record.external_id ||
                      record.source_record_uid ||
                      record.id
                    }}
                  </p>
                  <p class="text-xs text-n-slate-11">
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.RAW_LOOKUP.META', {
                        status: record.status,
                        redaction: record.redaction?.raw_payload,
                      })
                    }}
                  </p>
                  <div class="mt-2 flex flex-wrap gap-1">
                    <LaisStatusBadge
                      :label="
                        reconciliationStateLabel(record.reconciliation?.state)
                      "
                      :tone="reconciliationTone(record.reconciliation?.state)"
                    />
                    <LaisStatusBadge
                      v-if="record.status === 'duplicate'"
                      :label="
                        t(
                          'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.DUPLICATE_ITEM'
                        )
                      "
                      tone="warning"
                    />
                  </div>
                  <p
                    v-if="record.reconciliation?.imovel"
                    class="mt-1 text-xs text-n-slate-11"
                  >
                    {{
                      t(
                        'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.ITEM_META',
                        {
                          imovel: record.reconciliation.imovel.id,
                          listings: record.reconciliation.activeListingCount,
                        }
                      )
                    }}
                  </p>
                </div>
                <div class="flex gap-2">
                  <button
                    class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                    @click="loadRawRecord(record.id)"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.SNAPSHOT') }}
                  </button>
                  <button
                    class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                    :disabled="isActionLoading(`raw:${record.id}:replay`)"
                    @click="replayRawRecord(record)"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.REPLAY') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </LaisCard>

        <LaisCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  selectedRun
                    ? t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.SELECTED', {
                        source:
                          selectedRun.source?.source_key || selectedSourceId,
                      })
                    : t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.EMPTY_RUN')
                }}
              </p>
            </div>
            <span
              v-if="isLoadingDrifts"
              class="i-lucide-loader-circle size-4 animate-spin text-n-slate-11"
            />
          </div>

          <div class="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div class="rounded-lg bg-n-alpha-1 p-2">
              <p class="text-n-slate-11">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.SUMMARY.TOTAL') }}
              </p>
              <p class="font-semibold text-n-slate-12">
                {{ driftSummary.total || 0 }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-1 p-2">
              <p class="text-n-slate-11">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.SUMMARY.OPEN') }}
              </p>
              <p class="font-semibold text-n-amber-11">
                {{ driftSummary.open || 0 }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-1 p-2">
              <p class="text-n-slate-11">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.SUMMARY.BLOCKING') }}
              </p>
              <p class="font-semibold text-n-ruby-11">
                {{ driftSummary.blocking_active || 0 }}
              </p>
            </div>
          </div>

          <div class="mt-3 grid grid-cols-[1fr_auto] gap-2">
            <label class="flex flex-col text-xs text-n-slate-11">
              {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.FILTER_STATUS') }}
              <select
                v-model="driftFilterStatus"
                class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
              >
                <option value="open">
                  {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.OPEN') }}
                </option>
                <option value="acknowledged">
                  {{
                    t(
                      'PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.ACKNOWLEDGED'
                    )
                  }}
                </option>
                <option value="">
                  {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.STATUSES.ALL') }}
                </option>
              </select>
            </label>
            <button
              type="button"
              class="self-end rounded-lg border border-n-weak px-2 py-1.5 text-xs hover:bg-n-alpha-1"
              @click="fetchDrifts()"
            >
              {{ t('PROPERTIES.IMPORT_OPERATIONS.REFRESH') }}
            </button>
          </div>

          <label class="mt-3 flex flex-col text-xs text-n-slate-11">
            {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.RESOLUTION') }}
            <input
              v-model="driftResolution"
              type="text"
              class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
            />
          </label>

          <p v-if="driftError" class="mt-3 text-xs text-n-ruby-11">
            {{ driftError }}
          </p>
          <p
            v-else-if="!isLoadingDrifts && !hasDrifts"
            class="mt-3 text-xs text-n-slate-11"
          >
            {{ t('PROPERTIES.IMPORT_OPERATIONS.DRIFTS.EMPTY') }}
          </p>

          <div v-else class="mt-3 max-h-96 space-y-3 overflow-auto pr-1">
            <div
              v-for="drift in drifts"
              :key="drift.id"
              class="rounded-lg border border-n-weak p-3"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="break-words text-sm font-medium text-n-slate-12">
                    {{ drift.field || drift.externalId || drift.code }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ drift.message || drift.code }}
                  </p>
                </div>
                <LaisStatusBadge
                  :label="driftStatusLabel(drift.status)"
                  :tone="driftTone(drift)"
                  dot
                />
              </div>

              <div class="mt-3 flex flex-wrap gap-1">
                <span
                  v-for="step in driftFlowSteps(drift)"
                  :key="`${drift.id}-${step.key}`"
                  class="rounded-md px-2 py-1 text-xs"
                  :class="driftFlowStepClass(step.state)"
                >
                  {{ driftFlowStepLabel(step.key) }}
                </span>
              </div>

              <p class="mt-2 text-xs text-n-slate-11">
                {{
                  t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTION', {
                    suggestion: fieldSuggestionLabel(drift.suggestion),
                  })
                }}
              </p>

              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  v-for="action in DRIFT_REVIEW_ACTIONS"
                  :key="`${drift.id}-${action}`"
                  type="button"
                  class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1 disabled:opacity-50"
                  :disabled="isActionLoading(`drift:${drift.id}:${action}`)"
                  @click="reviewDrift(drift, action)"
                >
                  {{ driftActionLabel(action) }}
                </button>
              </div>
            </div>
          </div>
        </LaisCard>

        <LaisCard class="p-4">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{
                  selectedRun
                    ? t(
                        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.SELECTED',
                        {
                          count: tombstoneCandidates.length,
                          selected: selectedTombstoneCount,
                        }
                      )
                    : t(
                        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.EMPTY_RUN'
                      )
                }}
              </p>
            </div>
            <LaisStatusBadge
              v-if="hasTombstoneCandidates"
              :label="
                t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.READY', {
                  count:
                    selectedRun.operationalSummary.tombstones
                      .readyOnNextFullRunCount,
                })
              "
              tone="warning"
              dot
            />
          </div>

          <p
            v-if="selectedRun && !hasTombstoneCandidates"
            class="mt-4 text-xs text-n-slate-11"
          >
            {{ t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.EMPTY') }}
          </p>

          <div v-else-if="hasTombstoneCandidates" class="mt-4">
            <div class="grid grid-cols-3 gap-2 text-xs">
              <div class="rounded-lg bg-n-alpha-1 p-2">
                <p class="text-n-slate-11">
                  {{
                    t(
                      'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.UNREVIEWED'
                    )
                  }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ tombstoneReviewCounts.unreviewed }}
                </p>
              </div>
              <div class="rounded-lg bg-n-alpha-1 p-2">
                <p class="text-n-slate-11">
                  {{
                    t(
                      'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.APPROVED'
                    )
                  }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ tombstoneReviewCounts.approved }}
                </p>
              </div>
              <div class="rounded-lg bg-n-alpha-1 p-2">
                <p class="text-n-slate-11">
                  {{
                    t(
                      'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.STATUSES.SUPPRESSED'
                    )
                  }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ tombstoneReviewCounts.suppressed }}
                </p>
              </div>
            </div>

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                @click="selectAllTombstoneCandidates"
              >
                {{
                  t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.SELECT_ALL')
                }}
              </button>
              <button
                type="button"
                class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                @click="clearTombstoneCandidateSelection"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.CLEAR') }}
              </button>
            </div>

            <div
              class="mt-3 inline-flex rounded-lg border border-n-weak bg-n-background p-1"
            >
              <button
                v-for="decision in TOMBSTONE_REVIEW_DECISIONS"
                :key="decision"
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium"
                :class="
                  tombstoneReviewDecision === decision
                    ? 'bg-n-alpha-2 text-n-slate-12'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
                :aria-pressed="tombstoneReviewDecision === decision"
                @click="setTombstoneReviewDecision(decision)"
              >
                {{ tombstoneReviewStatusLabel(decision) }}
              </button>
            </div>

            <div class="mt-3 max-h-80 space-y-2 overflow-auto pr-1">
              <label
                v-for="candidate in tombstoneCandidates"
                :key="tombstoneCandidateKey(candidate)"
                class="flex gap-3 rounded-lg border border-n-weak p-3 text-sm"
                :class="{
                  'border-lais-primary bg-lais-primary-50/20':
                    isTombstoneCandidateSelected(candidate),
                }"
              >
                <input
                  type="checkbox"
                  class="mt-1 size-4"
                  :checked="isTombstoneCandidateSelected(candidate)"
                  :disabled="!candidate.externalId"
                  @change="toggleTombstoneCandidate(candidate)"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-start justify-between gap-2">
                    <p class="break-all font-medium text-n-slate-12">
                      {{ candidate.externalId || '-' }}
                    </p>
                    <div class="flex shrink-0 flex-wrap justify-end gap-1">
                      <LaisStatusBadge
                        :label="
                          tombstoneReviewStatusLabel(candidate.reviewStatus)
                        "
                        :tone="
                          tombstoneReviewStatusTone(candidate.reviewStatus)
                        "
                        dot
                      />
                      <LaisStatusBadge
                        v-if="candidate.readyOnNextFullRun"
                        :label="
                          t(
                            'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.READY_SHORT'
                          )
                        "
                        tone="warning"
                      />
                    </div>
                  </div>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{
                      t(
                        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.CANDIDATE_META',
                        {
                          source:
                            candidate.sourceKey || candidate.sourceId || '-',
                          missed: candidate.missedRuns,
                          grace: candidate.graceRuns,
                          snapshot: candidate.previousSnapshotId || '-',
                        }
                      )
                    }}
                  </p>
                  <p
                    v-if="candidate.reviewReason"
                    class="mt-1 text-xs text-n-slate-11"
                  >
                    {{
                      t(
                        'PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.REASON',
                        { reason: candidate.reviewReason }
                      )
                    }}
                  </p>
                </div>
              </label>
            </div>

            <p v-if="tombstoneReviewError" class="mt-3 text-xs text-n-ruby-11">
              {{ tombstoneReviewError }}
            </p>
            <p
              v-if="tombstoneDryRunResult"
              class="mt-3 text-xs text-n-slate-11"
            >
              {{
                t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.DRY_SUCCESS', {
                  count: tombstoneDryRunResult.reviewCount,
                  status: tombstoneReviewStatusLabel(
                    tombstoneDryRunResult.status
                  ),
                  provider: tombstoneDryRunResult.providerAcquisition,
                  sideEffect: tombstoneDryRunResult.sideEffect,
                })
              }}
            </p>
            <p v-if="tombstoneReviewResult" class="mt-3 text-xs text-n-teal-11">
              {{
                t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.RESULT', {
                  count: tombstoneReviewResult.reviewCount,
                  status: tombstoneReviewStatusLabel(
                    tombstoneReviewResult.status
                  ),
                  provider: tombstoneReviewResult.providerAcquisition,
                  sideEffect: tombstoneReviewResult.sideEffect,
                })
              }}
            </p>

            <div class="mt-3 flex flex-wrap gap-2">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-flask-conical"
                size="sm"
                :disabled="!hasSelectedTombstoneCandidates"
                @click="dryRunTombstoneReview"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.DRY_RUN') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-check-circle-2"
                size="sm"
                :disabled="!hasSelectedTombstoneCandidates"
                :is-loading="
                  isActionLoading(
                    `tombstone:${selectedRun.id}:${tombstoneReviewDecision}`
                  )
                "
                @click="reviewTombstoneCandidates"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.TOMBSTONE_REVIEW.SUBMIT') }}
              </LaisPrimaryButton>
            </div>
          </div>
        </LaisCard>

        <LaisCard class="p-4">
          <form class="flex flex-col gap-3" @submit.prevent="loadRawRecord()">
            <label class="flex flex-col text-xs text-n-slate-11">
              {{ t('PROPERTIES.IMPORT_OPERATIONS.RAW_LOOKUP.LABEL') }}
              <input
                v-model="rawRecordId"
                type="text"
                class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
              />
            </label>
            <LaisPrimaryButton
              type="submit"
              icon="i-lucide-search"
              :is-loading="isLoadingRaw"
            >
              {{ t('PROPERTIES.IMPORT_OPERATIONS.RAW_LOOKUP.SUBMIT') }}
            </LaisPrimaryButton>
          </form>

          <div
            v-if="rawRecord"
            class="mt-4 rounded-lg border border-n-weak p-3"
            :aria-label="t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.TITLE')"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{
                    rawRecord.external_id ||
                    rawRecord.source_record_uid ||
                    rawRecord.id
                  }}
                </p>
                <p class="mt-1 text-xs text-n-slate-11">
                  {{
                    t('PROPERTIES.IMPORT_OPERATIONS.RAW_LOOKUP.META', {
                      status: rawRecord.status,
                      redaction: rawRecord.redaction?.raw_payload,
                    })
                  }}
                </p>
              </div>
              <LaisStatusBadge
                :label="snapshotStateLabel(rawRecord.snapshotState)"
                :tone="snapshotStateTone(rawRecord.snapshotState)"
                dot
              />
            </div>

            <section class="mt-4 border-t border-n-weak pt-4">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.TITLE') }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.HELPER')
                    }}
                  </p>
                </div>
                <LaisStatusBadge
                  :label="reconciliationStateLabel(reconciliation.state)"
                  :tone="reconciliationTone(reconciliation.state)"
                  dot
                />
              </div>

              <div
                v-if="reconciliation.imovel"
                class="mt-3 rounded-lg bg-n-alpha-1 p-3 text-xs"
              >
                <p class="font-semibold text-n-slate-12">
                  {{
                    t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.IMOVEL', {
                      id: reconciliation.imovel.id,
                      title: reconciliation.imovel.titulo,
                    })
                  }}
                </p>
                <p class="mt-1 text-n-slate-11">
                  {{
                    t(
                      'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.IMOVEL_META',
                      {
                        type: reconciliation.imovel.tipo || '-',
                        city: reconciliation.imovel.cidade || '-',
                        district: reconciliation.imovel.bairro || '-',
                      }
                    )
                  }}
                </p>
              </div>
              <p v-else class="mt-3 text-xs text-n-slate-11">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.EMPTY') }}
              </p>

              <div
                v-if="reconciliation.duplicate?.present"
                class="mt-3 rounded-lg border border-n-amber-7 bg-n-amber-3 p-3 text-xs text-n-amber-12"
              >
                {{
                  t('PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.DUPLICATE', {
                    reason: reconciliation.duplicate.reason || '-',
                    raw: reconciliation.duplicate.duplicateOfRawRecordId || '-',
                  })
                }}
              </div>

              <div
                v-if="reconciliation.listings?.length"
                class="mt-3 grid gap-2"
              >
                <div
                  v-for="listing in reconciliation.listings"
                  :key="listing.id"
                  class="rounded-lg border border-n-weak p-3 text-xs"
                >
                  <div class="flex items-start justify-between gap-2">
                    <p class="font-medium text-n-slate-12">
                      {{
                        t(
                          'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.LISTING',
                          {
                            id: listing.id,
                            title: listing.titulo,
                          }
                        )
                      }}
                    </p>
                    <LaisStatusBadge
                      :label="listing.listing_status"
                      :tone="listing.available ? 'positive' : 'neutral'"
                    />
                  </div>
                  <p class="mt-1 text-n-slate-11">
                    {{
                      t(
                        'PROPERTIES.IMPORT_OPERATIONS.RECONCILIATION.LISTING_META',
                        {
                          mode: listing.modalidade,
                          version: listing.listing_version,
                          price: formatDiffValue(listing.price_amount),
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </section>

            <section class="mt-4 border-t border-n-weak pt-4">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.TITLE') }}
              </p>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.HELPER') }}
              </p>

              <p
                v-if="!hasFieldRows && !fieldErrors.length"
                class="mt-3 text-xs text-n-slate-11"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.EMPTY') }}
              </p>

              <div v-if="hasFieldRows" class="mt-3 grid gap-2">
                <div
                  v-for="row in fieldRows"
                  :key="row.field"
                  class="rounded-lg border border-n-weak p-3 text-xs"
                >
                  <div class="flex items-start justify-between gap-2">
                    <div>
                      <p class="font-semibold text-n-slate-12">
                        {{ row.field }}
                      </p>
                      <p class="mt-1 text-n-slate-11">
                        {{
                          t(
                            'PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.MAPPING',
                            {
                              source: fieldSourcesLabel(row),
                              policy: row.policy,
                            }
                          )
                        }}
                      </p>
                    </div>
                    <LaisStatusBadge
                      v-if="row.critical"
                      :label="
                        t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.CRITICAL')
                      "
                      tone="warning"
                    />
                  </div>

                  <p class="mt-2 break-words text-n-slate-11">
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.VALUE', {
                        value: formatDiffValue(row.normalizedValue),
                      })
                    }}
                  </p>

                  <div
                    v-if="row.diffEntries.length"
                    class="mt-2 flex flex-wrap gap-1"
                  >
                    <span
                      v-for="entry in row.diffEntries"
                      :key="`${row.field}-${entry.operation}-${entry.reason || entry.to || entry.value}`"
                      class="rounded-md bg-n-alpha-1 px-2 py-1 text-n-slate-11"
                    >
                      {{
                        t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.DIFF', {
                          operation: diffOperationLabel(entry.operation),
                          value: formatDiffValue(entry.to || entry.value),
                        })
                      }}
                    </span>
                  </div>

                  <div
                    v-if="row.errors.length"
                    class="mt-2 rounded-md bg-n-ruby-3 p-2 text-n-ruby-12"
                  >
                    <p
                      v-for="error in row.errors"
                      :key="`${row.field}-${error.code}-${error.message}`"
                    >
                      {{
                        t('PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.ERROR', {
                          code: error.code,
                          message: error.message || '-',
                        })
                      }}
                    </p>
                  </div>

                  <p class="mt-2 text-n-slate-11">
                    {{
                      t(
                        'PROPERTIES.IMPORT_OPERATIONS.FIELD_ERRORS.SUGGESTION',
                        {
                          suggestion: fieldSuggestionLabel(row.suggestion),
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </section>

            <section class="mt-4 border-t border-n-weak pt-4">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.TITLE') }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.TIMELINE', {
                        count: snapshotDetail.timelineCount || 0,
                      })
                    }}
                  </p>
                </div>
                <span class="text-xs text-n-slate-11">
                  {{ rawRecord.redaction?.snapshot_diff }}
                </span>
              </div>

              <p
                v-if="rawRecord.snapshotState === 'empty'"
                class="mt-3 text-xs text-n-slate-11"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.EMPTY') }}
              </p>
              <p
                v-else-if="rawRecord.snapshotState === 'partial'"
                class="mt-3 text-xs text-n-amber-11"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.PARTIAL') }}
              </p>
              <p
                v-else-if="rawRecord.snapshotState === 'quarantined'"
                class="mt-3 text-xs text-n-ruby-11"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.QUARANTINED') }}
              </p>

              <div v-if="latestSnapshot" class="mt-4 flex flex-col gap-4">
                <div>
                  <p class="text-xs font-semibold uppercase text-n-slate-11">
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.LATEST') }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{
                      t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.VERSION_META', {
                        version: latestSnapshot.snapshotVersion,
                        status: snapshotStateLabel(latestSnapshot.status),
                        date: formatDateTime(latestSnapshot.materializedAt),
                      })
                    }}
                  </p>
                </div>

                <div>
                  <p class="text-xs font-semibold uppercase text-n-slate-11">
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.DIFF') }}
                  </p>
                  <p
                    v-if="!hasSnapshotDiff"
                    class="mt-2 text-xs text-n-slate-11"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.DIFF_EMPTY') }}
                  </p>
                  <div v-else class="mt-2 flex flex-col gap-3">
                    <div
                      v-for="section in snapshotSections"
                      :key="section.operation"
                      class="rounded-lg bg-n-alpha-1 p-3"
                    >
                      <p class="text-xs font-semibold text-n-slate-12">
                        {{ diffOperationLabel(section.operation) }}
                      </p>
                      <div
                        v-for="entry in section.entries"
                        :key="`${section.operation}-${entry.field}`"
                        class="mt-2 border-t border-n-weak pt-2 text-xs text-n-slate-11"
                      >
                        <p class="font-medium text-n-slate-12">
                          {{ entry.field }}
                        </p>
                        <p v-if="entry.from !== undefined">
                          {{
                            t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.FROM_TO', {
                              from: formatDiffValue(entry.from),
                              to: formatDiffValue(entry.to),
                            })
                          }}
                        </p>
                        <p v-else>
                          {{
                            t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.VALUE', {
                              value: formatDiffValue(entry.to || entry.value),
                            })
                          }}
                        </p>
                        <p v-if="entry.reason">
                          {{
                            t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.REASON', {
                              reason: formatDiffValue(entry.reason),
                            })
                          }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <details>
                  <summary
                    class="cursor-pointer text-xs font-semibold text-n-slate-12"
                  >
                    {{ t('PROPERTIES.IMPORT_OPERATIONS.SNAPSHOT.NORMALIZED') }}
                  </summary>
                  <dl class="mt-2 grid gap-2 text-xs">
                    <div
                      v-for="entry in normalizedPayloadEntries(latestSnapshot)"
                      :key="entry.field"
                      class="grid grid-cols-[0.45fr_0.55fr] gap-2 border-t border-n-weak pt-2"
                    >
                      <dt class="text-n-slate-11">{{ entry.field }}</dt>
                      <dd class="break-words text-n-slate-12">
                        {{ entry.value }}
                      </dd>
                    </div>
                  </dl>
                </details>
              </div>
            </section>

            <div class="mt-3 flex flex-wrap gap-2">
              <button
                class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                :disabled="isActionLoading(`raw:${rawRecord.id}:replay`)"
                @click="replayRawRecord(rawRecord)"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.REPLAY') }}
              </button>
              <button
                class="rounded-lg border border-n-weak px-2 py-1 text-xs hover:bg-n-alpha-1"
                :disabled="isActionLoading(`raw:${rawRecord.id}:release`)"
                @click="releaseRawRecordQuarantine(rawRecord)"
              >
                {{ t('PROPERTIES.IMPORT_OPERATIONS.ACTIONS.RELEASE') }}
              </button>
            </div>
          </div>
        </LaisCard>
      </aside>
    </section>
  </main>
</template>
